import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, RefreshCw, Link2, CreditCard, Building2, 
  CheckCircle2, AlertCircle, ChevronRight, Loader2,
  Sparkles, Users, Banknote, ArrowRightLeft, Eye, Plus, X, Trash2, Upload, FileSpreadsheet
} from 'lucide-react';
import SqueegeeImport from '../components/SqueegeeImport';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SyncCentre = ({ onBack, setCurrentPage }) => {
  const [integrations, setIntegrations] = useState({});
  const [loading, setLoading] = useState(true);
  const [autoSyncPreview, setAutoSyncPreview] = useState(null);
  const [showAutoSyncModal, setShowAutoSyncModal] = useState(false);
  const [autoSyncLoading, setAutoSyncLoading] = useState(false);
  const [autoSyncResult, setAutoSyncResult] = useState(null);
  const [connectedBanks, setConnectedBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [showSqueegeeImport, setShowSqueegeeImport] = useState(false);

  useEffect(() => {
    loadIntegrations();
    loadConnectedBanks();
  }, []);

  const loadIntegrations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/integrations/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setIntegrations(data.connections || {});
      }
    } catch (err) {
      console.error('Error loading integrations:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadConnectedBanks = async () => {
    setBanksLoading(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/truelayer/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setConnectedBanks(data.banks || []);
      }
    } catch (err) {
      console.error('Error loading banks:', err);
    } finally {
      setBanksLoading(false);
    }
  };

  const connectNewBank = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/truelayer/connect`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.authorization_url) {
          window.location.href = data.authorization_url;
        }
      }
    } catch (err) {
      console.error('Error connecting bank:', err);
      alert('Failed to connect bank');
    }
  };

  const disconnectBank = async (bankId) => {
    if (!window.confirm('Are you sure you want to disconnect this bank?')) return;
    
    try {
      const token = localStorage.getItem('streakfree_token');
      await fetch(`${API_URL}/api/truelayer/disconnect?bank_id=${bankId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      // Reload banks
      loadConnectedBanks();
    } catch (err) {
      console.error('Error disconnecting bank:', err);
    }
  };

  const runBankDiagnostic = async () => {
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/truelayer/diagnostic`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const d = await res.json();
      const lines = [
        `Client ID in Render: ${d.client_id_preview}`,
        `Client Secret: ${d.client_secret_preview}`,
        `Redirect URI being sent: ${d.redirect_uri_will_be}`,
        `Source: ${d.redirect_uri_source}`,
        '',
        `TrueLayer auth test: ${d.credentials_authenticate_with_truelayer ? '✅ PASS' : '❌ FAIL'}`,
        `  Status: ${d.truelayer_auth_test_response?.status_code}`,
        `  Response: ${d.truelayer_auth_test_response?.body}`,
        '',
        `Next steps: ${d.next_steps}`
      ];
      alert(lines.join('\n'));
    } catch (err) {
      alert(`Diagnostic failed: ${err.message}`);
    }
  };

  const loadAutoSyncPreview = async () => {
    setAutoSyncLoading(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/integrations/stripe/customers/auto-sync-preview`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAutoSyncPreview(data);
        setShowAutoSyncModal(true);
      }
    } catch (err) {
      console.error('Error loading preview:', err);
    } finally {
      setAutoSyncLoading(false);
    }
  };

  const executeAutoSync = async () => {
    setAutoSyncLoading(true);
    try {
      const token = localStorage.getItem('streakfree_token');
      const res = await fetch(`${API_URL}/api/integrations/stripe/customers/auto-sync-execute`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAutoSyncResult(data);
        setAutoSyncPreview(null);
      }
    } catch (err) {
      console.error('Error executing auto-sync:', err);
    } finally {
      setAutoSyncLoading(false);
    }
  };

  const stripeConnected = integrations.stripe?.status === 'connected';
  const gcConnected = integrations.gocardless?.status === 'connected';

  return (
    <div className="h-full flex flex-col bg-zinc-50" data-testid="sync-centre-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-500 text-white">
        <div className="px-6 py-6">
          <div className="flex items-center gap-4">
            <button 
              onClick={onBack}
              className="p-2 hover:bg-white/10 rounded-xl transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold">Sync Centre</h1>
              <p className="text-purple-100">Sync customers and payments across all your connected apps</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-4xl mx-auto space-y-6">
          
          {/* Auto-Sync Card */}
          <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-3xl p-6 text-white shadow-xl">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Sparkles size={28} />
                </div>
                <div>
                  <h2 className="text-xl font-bold">Smart Auto-Sync</h2>
                  <p className="text-emerald-100 mt-1">
                    Automatically link customers with unique email matches
                  </p>
                </div>
              </div>
              <button
                onClick={loadAutoSyncPreview}
                disabled={!stripeConnected || autoSyncLoading}
                className="px-5 py-3 bg-white text-emerald-600 rounded-xl font-semibold hover:bg-emerald-50 disabled:bg-white/50 disabled:text-emerald-400 transition-colors flex items-center gap-2"
              >
                {autoSyncLoading ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Eye size={18} />
                )}
                Preview
              </button>
            </div>
            
            {!stripeConnected && (
              <div className="mt-4 p-3 bg-white/10 rounded-xl text-sm">
                Connect Stripe first to use Auto-Sync
              </div>
            )}
          </div>

          {/* Stripe Sync */}
          <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${stripeConnected ? 'border-purple-200' : 'border-zinc-200'}`}>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${stripeConnected ? 'bg-[#635bff]' : 'bg-zinc-200'}`}>
                    <span className="text-white font-bold text-lg">S</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 text-lg">Stripe Customers</h3>
                    <p className="text-zinc-500 text-sm">
                      {stripeConnected ? 'Sync and link your Stripe customers' : 'Connect Stripe to sync customers'}
                    </p>
                  </div>
                </div>
                
                {stripeConnected ? (
                  <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
                    <CheckCircle2 size={16} />
                    Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-zinc-400 text-sm">
                    <AlertCircle size={16} />
                    Not connected
                  </span>
                )}
              </div>
            </div>
            
            {stripeConnected && (
              <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100">
                <button
                  onClick={() => setCurrentPage('stripe-sync')}
                  className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-zinc-200 hover:border-purple-300 hover:bg-purple-50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <Users size={20} className="text-purple-500" />
                    <span className="font-medium text-zinc-900">View & Link Customers</span>
                  </div>
                  <ChevronRight size={20} className="text-zinc-400 group-hover:text-purple-500 transition-colors" />
                </button>
              </div>
            )}
          </div>

          {/* GoCardless Sync */}
          <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${gcConnected ? 'border-teal-200' : 'border-zinc-200'}`}>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${gcConnected ? 'bg-teal-500' : 'bg-zinc-200'}`}>
                    <CreditCard size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 text-lg">GoCardless Mandates</h3>
                    <p className="text-zinc-500 text-sm">
                      {gcConnected ? 'View and manage Direct Debit mandates' : 'Connect GoCardless to sync mandates'}
                    </p>
                  </div>
                </div>
                
                {gcConnected ? (
                  <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
                    <CheckCircle2 size={16} />
                    Connected
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-zinc-400 text-sm">
                    <AlertCircle size={16} />
                    Not connected
                  </span>
                )}
              </div>
            </div>
            
            {gcConnected && (
              <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100 space-y-3">
                <button
                  onClick={() => setCurrentPage('gocardless-sync')}
                  className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-zinc-200 hover:border-teal-300 hover:bg-teal-50 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <Users size={20} className="text-teal-500" />
                    <span className="font-medium text-zinc-900">View & Link Mandates</span>
                  </div>
                  <ChevronRight size={20} className="text-zinc-400 group-hover:text-teal-500 transition-colors" />
                </button>
              </div>
            )}
          </div>

          {/* Squeegee Sync */}
          <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
            <div className="p-5">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-emerald-500 to-teal-600">
                    <RefreshCw size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 text-lg">Sync from Squeegee</h3>
                    <p className="text-zinc-500 text-sm">Compare your Squeegee CSVs and apply only the changes you approve</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100">
              <button
                onClick={() => setCurrentPage('squeegee-sync')}
                className="w-full flex items-center justify-between p-4 bg-white rounded-xl border border-zinc-200 hover:border-emerald-300 hover:bg-emerald-50 transition-all group"
                data-testid="open-squeegee-sync-btn"
              >
                <div className="flex items-center gap-3">
                  <RefreshCw size={20} className="text-emerald-500" />
                  <span className="font-medium text-zinc-900">Compare Squeegee CSVs with StreakFree</span>
                </div>
                <ChevronRight size={20} className="text-zinc-400 group-hover:text-emerald-500 transition-colors" />
              </button>
            </div>
          </div>

          {/* CSV Import from Other CRMs */}
          <div className="bg-white rounded-2xl border border-amber-200 shadow-sm overflow-hidden">
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500">
                    <FileSpreadsheet size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 text-lg">Import from Other CRM</h3>
                    <p className="text-zinc-500 text-sm">
                      Import customers and jobs from Squeegee, Excel or other CSV files
                    </p>
                  </div>
                </div>
                
                <button
                  onClick={() => setShowSqueegeeImport(true)}
                  className="px-4 py-2 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-colors flex items-center gap-2"
                  data-testid="import-csv-btn"
                >
                  <Upload size={18} />
                  Import
                </button>
              </div>
            </div>
            
            <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100">
              <div className="text-sm text-zinc-600 space-y-1">
                <p className="font-medium">Supports:</p>
                <ul className="list-disc list-inside text-zinc-500 space-y-0.5">
                  <li>Squeegee Active Customers export</li>
                  <li>Jobs/Services CSV with customer matching</li>
                  <li>Any CSV with name, address, email columns</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Bank Connection */}
          <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${connectedBanks.length > 0 ? 'border-blue-200' : 'border-zinc-200'}`}>
            <div className="p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${connectedBanks.length > 0 ? 'bg-gradient-to-br from-blue-500 to-blue-600' : 'bg-zinc-200'}`}>
                    <Building2 size={24} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-zinc-900 text-lg">Bank Connections</h3>
                    <p className="text-zinc-500 text-sm">
                      {connectedBanks.length > 0 
                        ? `${connectedBanks.length} bank${connectedBanks.length > 1 ? 's' : ''} connected via Open Banking`
                        : 'Connect your bank to auto-match incoming payments'
                      }
                    </p>
                  </div>
                </div>
                
                {connectedBanks.length > 0 ? (
                  <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
                    <CheckCircle2 size={16} />
                    Connected
                  </span>
                ) : (
                  <button
                    onClick={connectNewBank}
                    className="px-4 py-2 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
                  >
                    Connect Bank
                  </button>
                )}
              </div>
            </div>
            
            {/* Connected Banks List */}
            {connectedBanks.length > 0 && (
              <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100 space-y-3">
                {connectedBanks.map((bank) => {
                  const isExpired = bank.status === 'expired';
                  return (
                  <div 
                    key={bank.id}
                    className={`bg-white rounded-xl border overflow-hidden ${isExpired ? 'border-amber-300 ring-1 ring-amber-200' : 'border-zinc-200'}`}
                    data-testid={`bank-card-${bank.bank_name?.toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isExpired ? 'bg-amber-100' : 'bg-blue-100'}`}>
                          <Building2 size={20} className={isExpired ? 'text-amber-600' : 'text-blue-600'} />
                        </div>
                        <div>
                          <p className="font-medium text-zinc-900">{bank.bank_name}</p>
                          <p className="text-sm text-zinc-500">{bank.account_name}</p>
                          {isExpired && (
                            <p className="text-xs text-amber-700 mt-0.5 font-medium" data-testid={`bank-expired-${bank.bank_name?.toLowerCase()}`}>
                              ⚠ Reconnect required — bank link expired
                            </p>
                          )}
                          {!isExpired && bank.last_refreshed_at && (
                            <p className="text-[11px] text-zinc-400 mt-0.5">
                              Auto-refreshed {new Date(bank.last_refreshed_at).toLocaleDateString('en-GB', {day:'numeric', month:'short'})}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        {isExpired && (
                          <button
                            onClick={connectNewBank}
                            className="px-3 py-1.5 bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium rounded-lg transition-colors"
                            data-testid={`reconnect-${bank.bank_name?.toLowerCase()}`}
                          >
                            Reconnect
                          </button>
                        )}
                        <button
                          onClick={() => disconnectBank(bank.id)}
                          className="p-2 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Disconnect bank"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                    {/* View Transactions for THIS bank */}
                    <button
                      onClick={() => {
                        // Store selected bank ID and navigate
                        sessionStorage.setItem('selectedBankId', bank.id);
                        setCurrentPage('bank-transactions');
                      }}
                      disabled={isExpired}
                      className={`w-full flex items-center justify-between px-4 py-3 border-t border-zinc-100 transition-all group ${isExpired ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed' : 'bg-zinc-50 hover:bg-blue-50'}`}
                      data-testid={`view-transactions-${bank.bank_name?.toLowerCase()}`}
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <Banknote size={16} className={isExpired ? 'text-zinc-400' : 'text-blue-500'} />
                        <span className="font-medium">View Transactions</span>
                      </div>
                      <ChevronRight size={16} className={isExpired ? 'text-zinc-300' : 'text-zinc-400 group-hover:text-blue-500 transition-colors'} />
                    </button>
                  </div>
                  );
                })}
                
                {/* Add Another Bank Button */}
                <button
                  onClick={connectNewBank}
                  className="w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed border-zinc-300 rounded-xl text-zinc-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                >
                  <Plus size={20} />
                  <span className="font-medium">Add Another Bank</span>
                </button>

                {/* Troubleshoot — only shown when there's at least one connection */}
                <button
                  onClick={runBankDiagnostic}
                  className="w-full text-xs text-zinc-500 hover:text-zinc-700 underline text-center mt-1"
                  data-testid="bank-diagnostic-button"
                >
                  Bank link not working? Run diagnostic
                </button>
              </div>
            )}
            
            {connectedBanks.length === 0 && (
              <div className="px-5 py-4 bg-zinc-50 border-t border-zinc-100 space-y-2">
                <div className="flex items-center gap-3 text-zinc-500 text-sm">
                  <Banknote size={18} />
                  <span>Connect via TrueLayer Open Banking to auto-reconcile payments</span>
                </div>
                <button
                  onClick={runBankDiagnostic}
                  className="text-xs text-zinc-500 hover:text-zinc-700 underline"
                  data-testid="bank-diagnostic-button-empty"
                >
                  Bank link not working? Run diagnostic
                </button>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Auto-Sync Preview Modal */}
      {showAutoSyncModal && autoSyncPreview && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-lg shadow-2xl overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 p-5 text-white">
              <div className="flex items-center gap-3">
                <Sparkles size={24} />
                <h2 className="text-xl font-bold">Auto-Sync Preview</h2>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-5 space-y-4">
              {/* All Synced State */}
              {autoSyncPreview.unique_matches === 0 ? (
                <div className="text-center py-6">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 size={32} className="text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-zinc-900">All Synced!</h3>
                  <p className="text-zinc-600 mt-2">
                    No new customers to auto-sync. All unique email matches have already been linked.
                  </p>
                  
                  <div className="bg-zinc-50 rounded-xl p-4 mt-6 space-y-2 text-sm text-left">
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Total Stripe customers:</span>
                      <span className="font-medium">{autoSyncPreview.total_stripe_customers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Already linked:</span>
                      <span className="font-medium text-emerald-600">{autoSyncPreview.already_linked}</span>
                    </div>
                    {autoSyncPreview.no_match > 0 && (
                      <div className="flex justify-between">
                        <span className="text-zinc-600">No CRM match:</span>
                        <span className="font-medium">{autoSyncPreview.no_match}</span>
                      </div>
                    )}
                    {(autoSyncPreview.duplicates_stripe + autoSyncPreview.duplicates_crm) > 0 && (
                      <div className="flex justify-between text-amber-600">
                        <span>Need manual review:</span>
                        <span className="font-medium">{autoSyncPreview.duplicates_stripe + autoSyncPreview.duplicates_crm}</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-emerald-600">{autoSyncPreview.unique_matches}</p>
                      <p className="text-sm text-emerald-700">Ready to Link</p>
                    </div>
                    <div className="bg-amber-50 rounded-xl p-4 text-center">
                      <p className="text-3xl font-bold text-amber-600">
                        {autoSyncPreview.duplicates_stripe + autoSyncPreview.duplicates_crm}
                      </p>
                      <p className="text-sm text-amber-700">Need Manual Review</p>
                    </div>
                  </div>
                  
                  <div className="bg-zinc-50 rounded-xl p-4 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Total Stripe customers:</span>
                      <span className="font-medium">{autoSyncPreview.total_stripe_customers}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Already linked:</span>
                      <span className="font-medium">{autoSyncPreview.already_linked}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-600">No CRM match:</span>
                      <span className="font-medium">{autoSyncPreview.no_match}</span>
                    </div>
                    {autoSyncPreview.duplicates_stripe > 0 && (
                      <div className="flex justify-between text-amber-600">
                        <span>Duplicate Stripe emails:</span>
                        <span className="font-medium">{autoSyncPreview.duplicates_stripe}</span>
                      </div>
                    )}
                    {autoSyncPreview.duplicates_crm > 0 && (
                      <div className="flex justify-between text-amber-600">
                        <span>Duplicate CRM emails:</span>
                        <span className="font-medium">{autoSyncPreview.duplicates_crm}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Warning */}
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle size={20} className="text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium">This will link {autoSyncPreview.unique_matches} customers automatically.</p>
                        <p className="mt-1 text-amber-700">
                          Only customers with unique email matches will be linked. 
                          Duplicates will be skipped for manual review.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
            
            {/* Actions */}
            <div className="px-5 pb-5 flex gap-3">
              <button
                onClick={() => {
                  setShowAutoSyncModal(false);
                  setAutoSyncPreview(null);
                }}
                className="flex-1 px-4 py-3 border border-zinc-300 rounded-xl font-medium text-zinc-700 hover:bg-zinc-50 transition-colors"
              >
                {autoSyncPreview.unique_matches === 0 ? 'Close' : 'Cancel'}
              </button>
              {autoSyncPreview.unique_matches > 0 && (
                <button
                  onClick={executeAutoSync}
                  disabled={autoSyncLoading}
                  className="flex-1 px-4 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 disabled:bg-zinc-300 transition-colors flex items-center justify-center gap-2"
                >
                  {autoSyncLoading ? (
                    <Loader2 size={18} className="animate-spin" />
                  ) : (
                    <ArrowRightLeft size={18} />
                  )}
                  Link {autoSyncPreview.unique_matches} Customers
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auto-Sync Result Modal */}
      {autoSyncResult && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 size={32} className="text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-zinc-900">Auto-Sync Complete!</h2>
              <p className="text-zinc-600 mt-2">
                Successfully linked <span className="font-semibold text-emerald-600">{autoSyncResult.linked_count}</span> customers
              </p>
              
              <button
                onClick={() => {
                  setAutoSyncResult(null);
                  setShowAutoSyncModal(false);
                }}
                className="mt-6 w-full px-4 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600 transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Squeegee/CSV Import Modal */}
      {showSqueegeeImport && (
        <SqueegeeImport
          onClose={() => setShowSqueegeeImport(false)}
          onImportComplete={(count) => {
            setShowSqueegeeImport(false);
            // Could show success message or refresh data
          }}
        />
      )}
    </div>
  );
};

export default SyncCentre;
